import { useTranslation } from 'react-i18next';
import {
  LiliAutoComplete,
  LiliButton,
  LiliButtonType,
  LiliTextInput,
  CloseButton,
  Text,
  FontWeight,
} from '@lili/lili-frontend-common';
import { ControllerWrapper } from '@Hooks/index';
import { FieldError } from '@Components/inputs/FieldError';
import { normalizeInputSpacesOnBlur, normalizeInputSpacesOnChange } from '@Services/utils/utils.ts';
import { CustomRadioButton } from '@Pages/happyFlowPages/eddQuestions/components/SelectionGroup.tsx';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import type { AdditionalLocationsFormData } from './hooks/useAdditionalLocationsLogic';
import type { LiliDropdownItem, ListItem } from '@lili/lili-frontend-common';
import type {
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayPrepend,
  UseFieldArrayRemove,
  UseFormClearErrors,
  UseFormReset,
  UseFormTrigger,
} from 'react-hook-form';

interface AdditionalBusinessLocationsProps {
  countries: LiliDropdownItem[] | undefined;
  fields: FieldArrayWithId<
    AdditionalLocationsFormData,
    typeof EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_LOCATIONS,
    'id'
  >[];
  append: UseFieldArrayAppend<AdditionalLocationsFormData>;
  prepend: UseFieldArrayPrepend<AdditionalLocationsFormData>;
  remove: UseFieldArrayRemove;
  hasAdditionalLocations: boolean;
  clearErrors: UseFormClearErrors<AdditionalLocationsFormData>;
  trigger: UseFormTrigger<AdditionalLocationsFormData>;
  reset: UseFormReset<AdditionalLocationsFormData>;
}

export const AdditionalBusinessLocations = ({
  countries,
  fields,
  append,
  remove,
  hasAdditionalLocations,
  clearErrors,
  trigger,
  reset,
}: AdditionalBusinessLocationsProps) => {
  const { t } = useTranslation('eddQuestions');

  return (
    <div className='w-full'>
      <div className='mb-6'>
        <ControllerWrapper
          name={EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_HAS_ADDITIONAL}>
          {({ onChange, value, error }) => (
            <div className='flex flex-col gap-2'>
              <CustomRadioButton
                name={EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_HAS_ADDITIONAL}
                label={t('common:no', 'No')}
                inputId='NO'
                checked={value === false}
                onChange={() => {
                  onChange(false);
                  clearErrors('EDD_ADDITIONAL_BUSINESS_LOCATIONS_LOCATIONS');
                  remove(Array.from({ length: fields.length }, (_, i) => i));
                  reset({
                    [EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_HAS_ADDITIONAL]: false,
                    [EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_LOCATIONS]: [],
                  });
                  setTimeout(() => {
                    trigger();
                  }, 0);
                }}
                value='false'
              />
              <CustomRadioButton
                name={EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_HAS_ADDITIONAL}
                label={t('common:yes', 'Yes')}
                inputId='YES'
                checked={value === true}
                onChange={() => onChange(true)}
                value='true'
              />
              <FieldError
                error={error}
                testId={`${EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_HAS_ADDITIONAL}-error`}
              />
            </div>
          )}
        </ControllerWrapper>
      </div>

      {hasAdditionalLocations && (
        <>
          <Text.H9 fontWeight={FontWeight.REGULAR} className='text-text-main-tertiary mb-2'>
            {t(
              'organic.additionalLocations.dynamicTitle',
              'Provide a list of all additional locations where your business operates'
            )}
          </Text.H9>

          {fields.map((field, index) => (
            <div key={field.id} className='flex items-start w-full relative'>
              <div className='flex w-full gap-4'>
                <div className='w-1/2'>
                  <ControllerWrapper
                    name={`${EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_LOCATIONS}.${index}.country`}>
                    {({
                      onChange,
                      value,
                    }: {
                      onChange: (value: ListItem) => void;
                      value: LiliDropdownItem;
                    }) => {
                      const selectedItemValue = value
                        ? { title: value.title, value: value.title }
                        : null;

                      return (
                        <div className='min-h-[76px] w-full'>
                          <LiliAutoComplete
                            inputId='COUNTRY-AUTO-COMPLETE'
                            shouldScrollOnOpen
                            disabled={false}
                            placeholder={'Country'}
                            onItemSelection={(item: { title: string; value: string }) => {
                              onChange(item);
                            }}
                            selectedItem={selectedItemValue}
                            autoCompleteItems={countries as ListItem[]}
                          />
                        </div>
                      );
                    }}
                  </ControllerWrapper>
                </div>

                <div className='w-1/2 relative'>
                  <ControllerWrapper
                    name={`${EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_LOCATIONS}.${index}.city`}>
                    {({ value, onBlur, onChange, error }) => (
                      <LiliTextInput
                        maxLength={30}
                        value={value}
                        onBlur={() => {
                          onChange(normalizeInputSpacesOnBlur(value));
                          onBlur();
                        }}
                        onChange={(val) => onChange(normalizeInputSpacesOnChange(val))}
                        id={`${EddEmbeddedFields.EDD_ADDITIONAL_BUSINESS_LOCATIONS_LOCATIONS}.${index}.city`}
                        title={t('organic.additionalLocations.city', 'City')}
                        errorMessage={error?.message}
                        isPublic
                      />
                    )}
                  </ControllerWrapper>
                </div>
              </div>
              {fields.length > 1 && (
                <div>
                  <CloseButton
                    className='mt-4 ml-[8px]'
                    onClick={() => remove(index)}
                    id={`remove-${index}`}
                  />
                </div>
              )}
            </div>
          ))}

          {fields.length < 3 && (
            <div className='max-w-[200px]'>
              <LiliButton
                id='add-another-country'
                className='px-[24px] py-[12px] text-[14px] leading-[14px]'
                type={LiliButtonType.SECONDARY}
                title={t('organic.additionalLocations.addAnother', 'Add Another Country')}
                onClick={() => append({ country: null, city: '' })}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
